import React, { useState } from 'react';
import Lottie from 'lottie-react';
import { useHistory } from 'react-router-dom';

import FSNList from '../Common/FSNList';
import ModelViewer from '../Common/ModelViewer';
import PopupDialogue from '../../components/common/PopupDialogue';
import CSVImport from './CSVImport';
import ArInfoActions from './ArInfoActions';
import HotspotFlyoutRight from '../../components/common/HotspotFlyoutRight';
import { useAuthStore } from '../../stores/authStore';

import { useInterval } from '../../hooks/useInterval';

import { useCommonStore } from '../../stores/commonStore';
import { useRequestorStore } from '../../stores/requestorStore';

import { generateGoLivePayload } from '../../utils/payloadUtil';

import { triggerGoLivePipeline, getRequestStatus } from '../../utils/api/requestApi';

import { GoLiveStatus, VisualQCStatus } from '../../utils/enums';

import bubbleLoaderAnimation from '../../static/bubble-loader.json';
import SideBarAnimation from '../../components/common/SideBarAnimation';

import { DISPLAY_COPIES, FSNS_ACTION } from '../../utils/constants/arMeta';
import { PipelineResponse } from '../../types/pipeline';
import { ROLES_MAPPING } from '../../utils/constants';
import { Routes } from '../../utils/constants/routes';

const DELAY = 5000;

const GoLive = () => {
    const history = useHistory();

    const sideBarOpen: boolean = useCommonStore((state) => state.sideBarOpen);
    const currentRequestId = useCommonStore((state) => state.currentRequestId);
    const fsns = useCommonStore((state) => state.goLiveFsns);
    const showFsnArInfoActions = useRequestorStore((state) => state.showFsnArInfoActions);

    const setActiveProduct = useCommonStore((state) => state.setActiveFSN);
    const setThreeDLiveFsns = useCommonStore((state) => state.setThreeDLiveFsns);
    const setGoLiveFsns = useCommonStore((state) => state.setGoLiveFsns);
    const setToastMessage = useCommonStore((state) => state.setToastMessage);
    const setCurrentRequestId = useCommonStore((state) => state.setCurrentRequestId);
    const setFlyoutRightOpen = useCommonStore((state) => state.setFlyoutRightOpen);
    const setShowFsnArInfoActions = useRequestorStore((state) => state.setShowFsnArInfoActions);

    const [isPendingRequest, setIsPendingRequest] = useState<boolean>(false);
    const [pipelinesResponse, setPipelinesResponse] = useState<PipelineResponse[]>([]);
    const [completeStatus, setCompleteStatus] = useState<GoLiveStatus>(GoLiveStatus.INIT);
    const [openDialogue, setOpenDialogue] = useState(false);
    const [fsnsAction, setFsnsAction] = useState('');

    const passedFSNs = fsns?.filter((item) => item.status && item.visualQCStatus === VisualQCStatus.PASSED);

    const activeRole = useAuthStore((state) => state.activeRole);

    const getGoLiveFsns = async () => {
        const requestParams = {
            requestId: currentRequestId
        };

        const response = await getRequestStatus<any>(requestParams);

        if (response && response.data.length >= 1) {
            let someModelStarted = response.data.some((item: PipelineResponse) => item.status === GoLiveStatus.STARTED);

            if (someModelStarted) return;

            let someModelFailed = response.data.some((item: PipelineResponse) => item.status === GoLiveStatus.FAILED);
            setPipelinesResponse(response.data);
            setCompleteStatus(someModelFailed ? GoLiveStatus.FAILED : GoLiveStatus.COMPLETED);
            resetData();
        }
    };

    const handleLogout = () => {
        localStorage.clear();
        // window.location.reload();
        history.push(Routes.LOGIN);
        // clearAndHandleLogout();
    };

    useInterval(getGoLiveFsns, isPendingRequest ? DELAY : null);

    const resetData = () => {
        setThreeDLiveFsns([]);
        setGoLiveFsns([]);
        setCurrentRequestId('');
        setIsPendingRequest(false);
        setActiveProduct('');
        setFlyoutRightOpen(false);
        setShowFsnArInfoActions(false);
        setFsnsAction('');
    };

    const handleOkLive = async () => {
        // const filtered = productsData?.filter((product) => product.fsn !== activeFsn);
        // setProducts(filtered);
        handleSettings(false);
        setOpenDialogue(false);

        try {
            const payload = generateGoLivePayload(fsns);
            const response = await triggerGoLivePipeline<any>(payload);

            if (response && response.data) {
                setCurrentRequestId(response.data.requestId);
                setIsPendingRequest(true);
            }
        } catch (e) {
            setToastMessage({ type: 'error', message: 'Something went wrong in going live' });
            console.log('error in going live:', e);
        }
    };

    const handleOkReset = () => {
        handleSettings(false);
        setGoLiveFsns([]);
    };

    const handleCancel = () => {
        setOpenDialogue(false);
    };

    const handleSettings = (value: boolean) => {
        setShowFsnArInfoActions(value);
        setFlyoutRightOpen(value);
        setFsnsAction('');
    };

    const handleFSNClose = () => {
        setShowFsnArInfoActions(false);
    };

    if ([GoLiveStatus.COMPLETED, GoLiveStatus.FAILED].includes(completeStatus) || isPendingRequest) {
        const failedFSNS = pipelinesResponse.filter((item) => item.status === 'FAILED').map((item) => item.uniqueId);

        const message = isPendingRequest ? (
            <div className="flex flex-col">
                <div className="flex justify-center items-center w-full h-auto">
                    <Lottie animationData={bubbleLoaderAnimation} loop={true} autoPlay={true} />
                </div>
                <p>Please wait! Live pipeline in progress</p>
            </div>
        ) : completeStatus === 'COMPLETED' ? (
            <p>Congratulations! All your models are live</p>
        ) : (
            <p>The following models failed: {failedFSNS.join(',')}! Please check`</p>
        );

        return <div className="w-full h-full flex justify-center items-center">{message}</div>;
    }

    const handleFsnsAction = (action: string) => {
        setFsnsAction(action);
        setOpenDialogue(true);
    };

    const popupData = {
        [FSNS_ACTION.GO_LIVE]: {
            ...DISPLAY_COPIES.golive,
            onOk: handleOkLive,
            onCancel: handleCancel
        },
        [FSNS_ACTION.RESET]: {
            ...DISPLAY_COPIES.reset,
            onOk: handleOkReset,
            onCancel: handleCancel
        }
    };

    return (
        <main className="flex container-height ">
            <SideBarAnimation open={sideBarOpen} type={ROLES_MAPPING[activeRole]} onClick={handleLogout} />

            <div className=" w-full">
                {fsns?.length ? (
                    <div className="container h-screen flex flex-row">
                        <FSNList fsns={passedFSNs} onFSNClick={() => handleSettings(true)} />
                        {fsnsAction ? (
                            <PopupDialogue
                                open={openDialogue}
                                title={popupData[fsnsAction].title}
                                description={popupData[fsnsAction].description}
                                okButtonText={popupData[fsnsAction].okButtonText}
                                cancelButtonText={popupData[fsnsAction].cancelButtonText}
                                onOk={popupData[fsnsAction].onOk}
                                onCancel={popupData[fsnsAction].onCancel}
                            />
                        ) : null}

                        {passedFSNs?.length ? (
                            <>
                                <HotspotFlyoutRight onClose={handleFSNClose}>
                                    <> {showFsnArInfoActions && <ArInfoActions onFsnsAction={handleFsnsAction} />}</>
                                </HotspotFlyoutRight>

                                <div className="w-full h-full flex flex-col justify-center items-center mb-2 mt-2 gap-8">
                                    <div className="w-5/6 h-3/6">
                                        <ModelViewer fsns={fsns} showCommentButton={false} />
                                    </div>
                                </div>
                            </>
                        ) : (
                            <div className="w-full flex justify-center items-center">
                                <h1>You dont have any FSNs to go live</h1>
                            </div>
                        )}
                    </div>
                ) : (
                    <CSVImport />
                )}
            </div>
        </main>
    );
};

export default React.memo(GoLive);
